import { calculateItemTotalPriceSummary, type CartItem } from '@src/domains/cart';
import { getSaleItemPackageDiscountCents, type DetailedSaleItem } from '@src/domains/sale-items';

export type SummaryLineItem = CartItem | DetailedSaleItem;

export const isCartLineItem = (item: SummaryLineItem): item is CartItem => 'costPreview' in item;

export const getSummaryItemPackageDiscountCents = (item: SummaryLineItem): number => {
  if (isCartLineItem(item)) {
    return calculateItemTotalPriceSummary(item).discount ?? 0;
  }

  if (!item.productPackageUnitId) {
    return 0;
  }

  return getSaleItemPackageDiscountCents(item);
};

export const getSummaryItemTotalCents = (item: SummaryLineItem): number => {
  if (isCartLineItem(item)) {
    return calculateItemTotalPriceSummary(item).total;
  }

  return item.totalPriceCurrencyInfo.amountCents;
};

export const getSummaryItemPreviousTotalCents = (item: SummaryLineItem): number | undefined => {
  if (isCartLineItem(item)) {
    const { previousPrice } = calculateItemTotalPriceSummary(item);
    return previousPrice;
  }

  const discount = getSummaryItemPackageDiscountCents(item);

  if (discount <= 0) {
    return undefined;
  }

  return item.fullPriceCurrencyInfo.amountCents;
};

const getSummaryPackagePreviousTotalCents = (item: SummaryLineItem): number => {
  if (isCartLineItem(item)) {
    return calculateItemTotalPriceSummary(item).previousPrice ?? 0;
  }

  return item.fullPriceCurrencyInfo.amountCents;
};

export type SummaryPackagePriceSummary = {
  totalCents: number;
  discountCents: number;
  previousCents?: number;
};

export const getSummaryPackagePriceSummary = (items: SummaryLineItem[]): SummaryPackagePriceSummary => {
  const summary = items.reduce(
    (acc, item) => ({
      totalCents: acc.totalCents + getSummaryItemTotalCents(item),
      discountCents: acc.discountCents + getSummaryItemPackageDiscountCents(item),
      previousCents: acc.previousCents + getSummaryPackagePreviousTotalCents(item),
    }),
    { totalCents: 0, discountCents: 0, previousCents: 0 },
  );

  return {
    totalCents: summary.totalCents,
    discountCents: summary.discountCents,
    previousCents: summary.discountCents > 0 ? summary.previousCents : undefined,
  };
};
